/* @deprecated Copied to app/modules/procurement/pages/vendors_management/vendor-profile/businessDetails/edit-business-details.tsx. This file still serves the existing /UProcure routes. */
/* eslint-disable jsx-a11y/anchor-is-valid */
/**
 * @file edit-business-details.tsx
 * @module app/modules/uProcure/vendors_management/vendor-profile/businessDetails/edit-business-details
 * @description Page component for managing edit business details in the vendors management section.
 *   Displays a list of edit business details with pagination, search functionality, and CRUD operations.
 *   Allows users to view the details of edit business details with warehouse context integration.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.1.1
 * @version 2.1.2
 */

import { Col, Form, Row } from "react-bootstrap";
import clsx from "clsx";
import { Profile } from "../../../../../../utils/string";
import { CustomSelect } from "../../../../../custom/Select/CustomSelect";
import AutoComplete from "../../../../../custom/autoComplete";
import React from "react";
import { CURRENCY, PAYMENTMODE, PAYMENTTERMS, TAXREGTYPES } from "../../../../../../utils/staticJSON";
import CustomDatePicker from "../../../../../custom/DateRange/CustomDatePicker";
import Method from "../../../../../../utils/methods";

const EditBusinessDetails = (props: any) => {
  const business = props?.editData?.business || {};
  const address = business.addressDetails || business.address || {};
  const taxDetails = business.taxDetails || {};

  const handleBasicChange = (e: any) => {
    props.onDetailsChange(e.target.name, e.target.value);
  };

  const daysOfWeek = [
    { value: 0, label: "Sunday" },
    { value: 1, label: "Monday" },
    { value: 2, label: "Tuesday" },
    { value: 3, label: "Wednesday" },
    { value: 4, label: "Thursday" },
    { value: 5, label: "Friday" },
    { value: 6, label: "Saturday" },
  ];

  const formatWorkingDays = (days: any[]) => {
    if (!days || days.length === 0) return "";

    // Convert days to numbers if they are strings from API
    const numericDays = days.map(d => typeof d === 'object' ? d.day : d).map(Number).sort((a, b) => a - b);
    
    const groups: number[][] = [];
    let currentGroup: number[] = [numericDays[0]];

    for (let i = 1; i < numericDays.length; i++) {
      if (numericDays[i] === numericDays[i - 1] + 1) {
        currentGroup.push(numericDays[i]);
      } else {
        groups.push(currentGroup);
        currentGroup = [numericDays[i]];
      }
    }
    groups.push(currentGroup);

    return groups
      .map((group) => {
        if (group.length === 1) {
          return daysOfWeek.find((d) => d.value === group[0])?.label;
        } else {
          const firstDay = daysOfWeek.find((d) => d.value === group[0])?.label;
          const lastDay = daysOfWeek.find((d) => d.value === group[group.length - 1])?.label;
          return `${firstDay}-${lastDay}`;
        }
      })
      .join(", ");
  };

  const handleWorkingDayChange = (dayValue: number) => {
    const currentDays = Array.isArray(business.workingDays) 
      ? business.workingDays.map((d: any) => typeof d === 'object' ? d.day : d).map(Number)
      : [];
    
    let updatedDays;
    if (currentDays.includes(dayValue)) {
      updatedDays = currentDays.filter((d: number) => d !== dayValue);
    } else {
      updatedDays = [...currentDays, dayValue].sort((a, b) => a - b);
    }

    props.onDetailsChange("workingDays", updatedDays);
  };

  return (
    <div className="card-body bg-light pt-9 pb-0">
      <Row>
        {/* ================= VENDOR DETAILS ================= */}

        <Col lg={6} className="mb-5">
          <label className="fs-16 fw-500 text-dark mb-2">Vendor Name</label>
          <input
            className="form-control form-control-custom bg-white"
            name="ownerName"
            value={business.ownerName || ""}
            onChange={handleBasicChange}
          />
        </Col>

        <Col lg={6} className="mb-5">
          <label className="fs-16 fw-500 text-dark mb-2">Contact Person</label>
          <input
            className="form-control form-control-custom bg-white"
            name="name"
            value={business.name || ""}
            onChange={handleBasicChange}
          />
        </Col>

        <Col lg={6} className="mb-5">
          <label className="fs-16 fw-500 text-dark mb-2">Phone Number</label>
          <input
            disabled
            className="form-control form-control-custom bg-white"
            value={`${business.phoneCountry || ""} ${business.phone || ""}`}
          />
        </Col>

        <Col lg={6} className="mb-5">
          <label className="fs-16 fw-500 text-dark mb-2">Email</label>
          <input
            disabled
            className="form-control form-control-custom bg-white"
            value={business.email || ""}
          />
        </Col>

        <Col lg={6} className="mb-5">
          <label className="fs-16 fw-500 text-dark mb-2">TIN No</label>
          <input
            className="form-control form-control-custom bg-white"
            name="tinNumber"
            value={business.tinNumber || ""}
            onChange={handleBasicChange}
          />
        </Col>
        <Col lg={6} className="mb-5">
          <label className="fs-16 fw-500 text-dark mb-2">VAT No</label>
          <input
            className="form-control form-control-custom bg-white"
            name="vatNumber"
            value={business.vatNumber || ""}
            onChange={handleBasicChange}
          />
        </Col>

        {/* ================= BUSINESS DETAILS ================= */}

        <Col lg={6} className="mb-5">
          <label className="fs-16 fw-500 text-dark mb-2">Legal Name</label>
          <input
            className="form-control form-control-custom bg-white"
            name="legalName"
            value={business.legalName || ""}
            onChange={handleBasicChange}
          />
        </Col>

        <Col lg={6} className="mb-5">
          <label className="fs-16 fw-500 text-dark mb-2">Trade Name</label>
          <input
            className="form-control form-control-custom bg-white"
            name="tradeName"
            value={business.tradeName || ""}
            onChange={handleBasicChange}
          />
        </Col>

        <Col lg={12} className="mb-5">
          <label className="fs-16 fw-500 text-dark mb-2">
            Business Address
          </label>
          <AutoComplete
            address={address.addressLine1 || ""}
            handleAddressChange={props.handleAddressChange}
            lat={business.lat || ""}
            lng={business.lng || ""}
          />
        </Col>

        <Col lg={4} className="mb-5">
          <label className="fs-16 fw-500 text-dark mb-2">City</label>
          <input
            className="form-control form-control-custom bg-white"
            name="addressDetails.city"
            value={address.city || ""}
            onChange={handleBasicChange}
          />
        </Col>

        <Col lg={4} className="mb-5">
          <label className="fs-16 fw-500 text-dark mb-2">State / Region</label>
          <input
            className="form-control form-control-custom bg-white"
            name="addressDetails.state"
            value={address.state || ""}
            onChange={handleBasicChange}
          />
        </Col>

        <Col lg={4} className="mb-5">
          <label className="fs-16 fw-500 text-dark mb-2">PIN / ZIP Code</label>
          <input
            className="form-control form-control-custom bg-white"
            name="addressDetails.postalCode"
            value={address.postalCode || ""}
            onChange={handleBasicChange}
          />
        </Col>

        <Col lg={6} className="mb-5">
          <label className="fs-16 fw-500 text-dark mb-2">Country</label>
          <input
            className="form-control form-control-custom bg-white"
            name="addressDetails.countryName"
            value={address.countryName || ""}
            onChange={handleBasicChange}
            disabled
          />
        </Col>

        <Col lg={6} className="mb-5">
          <label className="fs-16 fw-500 text-dark mb-2">
            Tax Registration Type
          </label>
          <CustomSelect
            backgroundColor="white"
            options={TAXREGTYPES}
            default={
                taxDetails.type
                ? TAXREGTYPES.find((item) => item.value == taxDetails.type)
                : ""
            }
            onChange={(e: any) =>
                props.onDetailsChange("taxDetails.type", e.value)
            }
            isMulti={false}
          />
        </Col>

        <Col lg={6} className="mb-5">
          <label className="fs-16 fw-500 text-dark mb-2">Tax ID Number</label>
          <input
            className="form-control form-control-custom bg-white"
            name="taxDetails.number"
            value={taxDetails.number || ""}
            onChange={handleBasicChange}
          />
        </Col>

        <Col lg={6} className="mb-5">
          <label className="fs-16 fw-500 text-dark mb-2">Currency</label>
          <CustomSelect
            backgroundColor="white"
            options={CURRENCY}
            default={
                business.currency
                ? CURRENCY.find((item) => item.value == business.currency)
                : ""
            }
            onChange={(e: any) =>
                props.onDetailsChange("currency", e.value)
            }
            isMulti={false}
          />
        </Col>

        <Col lg={6} className="mb-5">
          <label className="fs-16 fw-500 text-dark mb-2">Payment Terms</label>
          <CustomSelect
            backgroundColor="white"
            options={PAYMENTTERMS}
            default={
                business.paymentTerms
                ? PAYMENTTERMS.find((item) => item.value == business.paymentTerms)
                : ""
            }
            onChange={(e: any) =>
                props.onDetailsChange("paymentTerms", e.value)
            }
            isMulti={false}
          />
        </Col>

        <Col lg={6} className="mb-5">
          <label className="fs-16 fw-500 text-dark mb-2">
            Preferred Payment Mode
          </label>
          <CustomSelect
            backgroundColor="white"
            options={PAYMENTMODE}
            default={
                business.paymentMode
                ? PAYMENTMODE.find((item) => item.value == business.paymentMode)
                : ""
            }
            onChange={(e: any) =>
                props.onDetailsChange("paymentMode", e.value)
            }
            isMulti={false}
          />
        </Col>

        <Col lg={6} className="mb-5">
          <label className="fs-16 fw-500 text-dark mb-2">Credit Limit</label>
          <input
            type="number"
            className="form-control form-control-custom bg-white"
            name="creditLimit"
            value={business.creditLimit || ""}
            onChange={handleBasicChange}
          />
        </Col>

        <Col lg={12} className="mb-5">
          <label className="fs-16 fw-500 text-dark mb-2">Annual Contract</label>
          <div className="d-flex align-items-center gap-5 mb-3">
            <div className="form-check form-check-custom form-check-solid">
              <input
                className="form-check-input"
                type="radio"
                id="annualContractNoEdit"
                checked={!business.hasAnnualContract && !business.contractValidFrom && !business.contractValidTo}
                onChange={() => {
                  props.onDetailsChange("hasAnnualContract", false);
                  props.onDetailsChange("contractValidFrom", "");
                  props.onDetailsChange("contractValidTo", "");
                }}
              />
              <label className="form-check-label fs-16 fw-600 text-dark" htmlFor="annualContractNoEdit">
                No
              </label>
            </div>
            <div className="form-check form-check-custom form-check-solid">
              <input
                className="form-check-input"
                type="radio"
                id="annualContractYesEdit"
                checked={business.hasAnnualContract || !!business.contractValidFrom || !!business.contractValidTo}
                onChange={() => props.onDetailsChange("hasAnnualContract", true)}
              />
              <label className="form-check-label fs-16 fw-600 text-dark" htmlFor="annualContractYesEdit">
                Yes
              </label>
            </div>
          </div>
          {(business.hasAnnualContract || business.contractValidFrom || business.contractValidTo) && (
            <div className="mt-3 min-w-280px max-w-280px">
              <label className="fs-14 fw-500 mb-2">Contract Date Range</label>
              <CustomDatePicker
                selectsRange={true}
                startDate={business.contractValidFrom ? new Date(business.contractValidFrom) : null}
                endDate={business.contractValidTo ? new Date(business.contractValidTo) : null}
                onChange={(dates: any) => {
                  const [start, end] = dates;
                  props.onDetailsChange("contractValidFrom", start ? Method.convertDateToFormat(start, "YYYY-MM-DD") : "");
                  props.onDetailsChange("contractValidTo", end ? Method.convertDateToFormat(end, "YYYY-MM-DD") : "");
                }}
                dateFormat="dd/MM/yyyy"
                placeholderText="Select contract dates"
                inputTextBG="bg-white"
              />
            </div>
          )}
        </Col>

        <Col lg={6} className="mb-5">
          <label className="fs-16 fw-500 text-dark mb-2">Lead Duration</label>
          <input
            type="number"
            className="form-control form-control-custom bg-white"
            name="leadDuration"
            value={business.leadDuration ?? ""}
            onChange={handleBasicChange}
            placeholder="Type here…"
          />
        </Col>

        <Col lg={12} className="mb-5">
          <label className="fs-16 fw-500 text-dark mb-2">
            Working Days
            {Array.isArray(business.workingDays) && business.workingDays.length > 0 && (
              <span className="ms-3 p-2 bg-white border border-secondary border-r10px">
                <span className="fs-14 fw-600 text-muted">Selected: </span>
                <span className="fs-16 fw-600 text-dark">
                  {formatWorkingDays(business.workingDays)}
                </span>
              </span>
            )}
          </label>
          <div className="border border-r10px bg-white p-4 mt-4">
            <Row>
              {daysOfWeek.map((day) => {
                const isChecked = Array.isArray(business.workingDays) && 
                  business.workingDays.map((d: any) => typeof d === 'object' ? d.day : d).map(Number).includes(day.value);
                return (
                  <Col xs={6} md={3} key={day.value} className="mb-3">
                    <Form.Check
                      type="checkbox"
                      id={`day-${day.value}`}
                      label={day.label}
                      checked={isChecked}
                      onChange={() => handleWorkingDayChange(day.value)}
                      className="fs-16 fw-500"
                    />
                  </Col>
                );
              })}
            </Row>
          </div>
        </Col>

        <Col lg={12} className="mb-5">
          <label className="fs-16 fw-500 text-dark mb-2">Vendor Category</label>
          <CustomSelect
            backgroundColor={"white"}
            default={
              props.editData.business.categories.length
                ? props.editData.business.categories.map((catVal: any) => {
                    return {
                      value: catVal.reference.title,
                      label: catVal.reference.title,
                      title: catVal.reference.title,
                      _id: catVal.reference._id,
                      image: catVal.reference.image,
                    };
                  })
                : []
            }
            options={
              props?.category?.length
                ? props?.category?.map((catVal: any) => {
                    return {
                      value: catVal.title,
                      label: catVal.title,
                      title: catVal.title,
                      _id: catVal._id,
                      image: catVal.image,
                      uTradeCategoryId: catVal.uTradeCategoryId
                    };
                  })
                : []
            }
            onChange={(e: any, action: any) => {
              props.onCategorySelect(e, action);
            }}
            isMulti={true}
          />
        </Col>

        <Col lg={12} className="mb-5">
          <label className="fs-16 fw-500 text-dark mb-2">Business Type</label>
          <CustomSelect
            backgroundColor={"white"}
            default={
              props.editData.business.types.length
                ? props.editData.business.types.map((val: any) => {
                    return {
                      value: val.reference.title,
                      label: val.reference.title,
                      title: val.reference.title,
                      _id: val.reference._id,
                      image: val.reference.image,
                    };
                  })
                : []
            }
            options={
              props.businessTypes?.length
                ? props.businessTypes.map((val: any) => {
                    return {
                      value: val.title,
                      label: val.title,
                      title: val.title,
                      _id: val._id,
                      image: val.image,
                    };
                  })
                : []
            }
            onChange={(e: any): void => props.onBusinessTypesSelect(e)}
            isMulti={false}
          />
        </Col>

        <Col lg={12} className="mb-5">
          <label className="fs-16 fw-500 text-dark mb-2">
            Notes / About Business
          </label>
          <textarea
            rows={4}
            className="form-control form-control-custom bg-white"
            name="about"
            value={business.about || ""}
            onChange={handleBasicChange}
          />
        </Col>

        {/* ================= BANK DETAILS ================= */}

        {business.banks?.length ? (
          business.banks.map((bank: any, index: number) => (
            <React.Fragment key={index}>
              <Col lg={12} className="mb-3">
                <label className="fs-16 fw-500 text-dark">
                  Bank Details {index + 1}
                </label>
              </Col>

              <Col lg={6} className="mb-5">
                <label className="fs-16 fw-500 text-dark mb-2">Bank Name</label>
                <input
                  className="form-control form-control-custom bg-white"
                  name={`banks.${index}.bankName`}
                  value={bank.bankName || ""}
                  onChange={handleBasicChange}
                />
              </Col>

              <Col lg={6} className="mb-5">
                <label className="fs-16 fw-500 text-dark mb-2">
                  Bank Branch
                </label>
                <input
                  className="form-control form-control-custom bg-white"
                  name={`banks.${index}.branchName`}
                  value={bank.branchName || ""}
                  onChange={handleBasicChange}
                />
              </Col>

              <Col lg={6} className="mb-5">
                <label className="fs-16 fw-500 text-dark mb-2">
                  Account Number
                </label>
                <input
                  className="form-control form-control-custom bg-white"
                  name={`banks.${index}.accountNumber`}
                  value={bank.accountNumber || ""}
                  onChange={handleBasicChange}
                />
              </Col>

              <Col lg={6} className="mb-5">
                <label className="fs-16 fw-500 text-dark mb-2">
                  Identifier Code
                </label>
                <input
                  className="form-control form-control-custom bg-white"
                  name={`banks.${index}.identifierCode`}
                  value={bank.identifierCode || ""}
                  onChange={handleBasicChange}
                />
              </Col>

              <Col lg={6} className="mb-5">
                <label className="fs-16 fw-500 text-dark mb-2">
                  Tigo Number
                </label>
                <input
                  className="form-control form-control-custom bg-white"
                  name={`banks.${index}.tigoNumber`}
                  value={bank.tigoNumber || ""}
                  onChange={handleBasicChange}
                />
              </Col>
            </React.Fragment>
          ))
        ) : (
          <></>
        )}
      </Row>
    </div>
  );
};

export { EditBusinessDetails };
